import React, { useMemo, useState } from 'react';
import { Checkbox, Collapse, Input } from 'antd';
import { Box } from '@mui/material';
import { RightOutlined } from "@ant-design/icons";
import { translate } from '../../helper';

const { Panel } = Collapse;

const PermissionSelector = ({
    permissionsList = [],
    value = [],
    onChange,
}) => {

    const [search, setSearch] = useState('');
    const [activePanels, setActivePanels] = useState([]);

    /* ---------------- GROUP PERMISSIONS ---------------- */

    const groupedPermissions = useMemo(() => {
        return permissionsList.reduce((acc, perm) => {
            const label = perm.app_label;

            if (!acc[label]) {
                acc[label] = { label, permissions: [] };
            }

            acc[label].permissions.push(perm);
            return acc;
        }, {});
    }, [permissionsList]);

    /* ---------------- SEARCH FILTER ---------------- */

    const filteredGroups = useMemo(() => {
        if (!search) return groupedPermissions;

        const result = {};
        const lower = search.toLowerCase();

        Object.values(groupedPermissions).forEach(group => {
            const matched = group.permissions.filter(p =>
                p.name.toLowerCase().includes(lower)
            );

            if (matched.length) {
                result[group.label] = {
                    ...group,
                    permissions: matched
                };
            }
        });

        setActivePanels(Object.keys(result));
        return result;
    }, [search, groupedPermissions]);

    /* ---------------- HELPERS ---------------- */

    const togglePermission = (id, checked) => {

        const perm = permissionsList.find(p => p.id === id);
        if (!perm) return;

        const updated = applyReadEditDependency(perm, checked);

        onChange(updated);
    };

    const toggleGroup = (groupPermissions, checked) => {
        const ids = groupPermissions.map(p => p.id);

        if (checked) {
            onChange([...new Set([...value, ...ids])]);
        } else {
            onChange(value.filter(v => !ids.includes(v)));
        }
    };

    const isGroupFull = (groupPermissions) =>
        groupPermissions.every(p => value.includes(p.id));

    const isGroupPartial = (groupPermissions) => {
        const count = groupPermissions.filter(p =>
            value.includes(p.id)
        ).length;

        return count > 0 && count < groupPermissions.length;
    };

    const findReadPermission = (permission) => {
        if (!permission.codename.includes('_edit_')) return null;

        const readCodename = permission.codename.replace(
            '_edit_',
            '_read_'
        );

        return permissionsList.find(
            p => p.codename === readCodename
        );
    };
    const findEditPermission = (permission) => {
        if (!permission.codename.includes('_read_')) return null;

        const editCodename = permission.codename.replace(
            '_read_',
            '_edit_'
        );

        return permissionsList.find(
            p => p.codename === editCodename
        );
    };
    const applyReadEditDependency = (perm, checked) => {

        let updated = [...value];

        if (checked) {

            // add selected
            updated.push(perm.id);

            // ✅ EDIT ⇒ READ
            const readPerm = findReadPermission(perm);
            if (readPerm) {
                updated.push(readPerm.id);
            }

        } else {

            // remove selected
            updated = updated.filter(id => id !== perm.id);

            // ✅ removing READ removes EDIT
            const editPerm = findEditPermission(perm);
            if (editPerm) {
                updated = updated.filter(id => id !== editPerm.id);
            }
        }

        return [...new Set(updated)];
    };

    /* ---------------- UI ---------------- */

    return (
        <>
            <Input
                placeholder="Search permissions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ marginBottom: 'calc(var(--sp-md2)*1.50943)' }}
            />

            {/* GLOBAL SELECT */}
            <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'calc(var(--sp-md2)*1.132075)', alignItems: 'center' }}>
                <Checkbox
                    className='roles_permission_checkbox'
                    checked={value.length === permissionsList.length}
                    indeterminate={
                        value.length > 0 &&
                        value.length < permissionsList.length
                    }
                    onChange={(e) =>
                        onChange(
                            e.target.checked
                                ? permissionsList.map(p => p.id)
                                : []
                        )
                    }
                    style={{ fontWeight: 600, fontSize: "calc(var(--fs-xs)*1.50943)" }}
                >
                    Select All Permissions
                </Checkbox>
                <span style={{ fontSize: "calc(var(--fs-xs)*1.32075)" }}>
                    {value.length} / {permissionsList.length} Selected
                </span>
            </Box>

            {search && Object.keys(filteredGroups).length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: 'calc(var(--sp-lg)*2) 0',
                    color: '#9ca3af',
                    fontSize: 'calc(var(--fs-xs)*1.50943)',
                    fontWeight: 500,
                }}>
                    {translate('noSearchFound')}
                </div>
            )}

            <Collapse
                ghost
                activeKey={activePanels}
                onChange={setActivePanels}
                expandIconPosition="start"
                expandIcon={({ isActive }) => (
                    <RightOutlined
                        style={{
                            fontSize: "calc(var(--fs-xs) * 1.32075)",
                            transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease"
                        }}
                    />
                )}
                motion={{
                    motionAppear: true,
                    motionEnter: true,
                    motionLeave: true
                }}
            >
                {Object.values(filteredGroups).map(group => {

                    const groupPermissions = group.permissions;

                    return (
                        <Panel
                            key={group.label}
                            header={
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%'
                                    }}
                                >
                                    <Checkbox
                                        className='roles_permission_checkbox'
                                        checked={isGroupFull(groupPermissions)}
                                        indeterminate={isGroupPartial(groupPermissions)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            toggleGroup(
                                                groupPermissions,
                                                e.target.checked
                                            );
                                        }}
                                        style={{ fontWeight: 600, fontSize: "calc(var(--fs-xs)*1.32075)", textTransform: 'capitalize' }}
                                    >
                                        {group.label.replaceAll('_', ' ')}
                                    </Checkbox>

                                    <span style={{ fontSize: "calc(var(--fs-xs)*1.32075)" }}>
                                        {
                                            groupPermissions.filter(p =>
                                                value.includes(p.id)
                                            ).length
                                        }
                                        /
                                        {groupPermissions.length}
                                    </span>
                                </div>
                            }
                        >
                            <div
                                style={{
                                    paddingLeft: 'calc(var(--sp-lg)*2.5)',
                                    // borderLeft: '2px solid #f0f0f0'
                                }}
                            >
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 'calc(var(--sp-md2)*1.50943)'
                                    }}
                                >
                                    {groupPermissions.map(perm => (
                                        <Checkbox
                                            className='roles_permission_checkbox'
                                            key={perm.id}
                                            checked={value.includes(perm.id)}
                                            onChange={(e) =>
                                                togglePermission(
                                                    perm.id,
                                                    e.target.checked
                                                )
                                            }
                                            style={{ fontWeight: 500, fontSize: "calc(var(--fs-xs)*1.32075)" }}

                                        >
                                            {perm.name}
                                        </Checkbox>
                                    ))}
                                </div>
                            </div>
                        </Panel>
                    );
                })}
            </Collapse>
        </>
    );
};

export default PermissionSelector;